/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { SplitPromptSequenceProvider } from '@/providers/SplitPromptSequenceProvider';
import { actionTypes } from '@/reducers/actionTypes/actionTypes';
import { constructPromptCriteriaFilterString } from '@/utils/constructPromptCriteriaFilterString';
import { convertToUTCDate } from '@/utils/convertToUTCDate';
import ErrorBoundary from '@/utils/ErrorBoundray';
import { getPlaceholderText } from '@/utils/generalUtils';
import { extractData } from '@/utils/promptSequenceViewDataVariation';
import { findNodeByKey } from '@/utils/sankeyBreakdownUtils';
import { shallowComparator, strToArray } from '@/utils/tracesUtils';
import { getTraceTimeRange } from '@/utils/traceTimeRange';
import { EvaluateContextValue, PostEvalsQueryBodyType } from 'types/api-data';
import {
  CriteriaPayloadType,
  FilterCriteriaGroup,
  IPrompt,
  TIdSpecificEvalConfig,
} from 'types/prompt';

import { removeFilterDuplicates } from '@/lib/helper';
import { cn } from '@/lib/utils';
import { useEvals } from '@/hooks/useEvals';
import { usePrompts } from '@/hooks/usePrompts';
import { useTraceId } from '@/hooks/useTraceID';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelect } from '@/components/combobox';
import { CustomButton } from '@/components/common';
import { FindAppContext } from '@/components/component-details-page/comp-details-header/comp-details-header';
import EvalCriteriaFilter from '@/components/eval/criteria-filter';
import { EvalModal } from '@/components/eval/eval-modal';
import { System } from '@/components/icons';
import { PromptSequenceView } from '@/components/prompt-sequence-view';
import { PromptsListing } from '@/components/prompts-listing';
import { itemsPerPage } from '@/components/prompts-listing/prompts-listing';
import PromptsTraceIdSearchTab from '@/components/prompts-listing/prompts-trace-id-search-tab';
import Loader from '@/components/spinner/loader';
import { ToolbarContainer } from '@/components/toolbar';
import { TreeDataItem, TreeDropdown } from '@/components/treeview-dropdown';

import { AddPromptCriteriaFilters } from './add-criteria-filter/add-promt-criteria-filters';
import { DateRange } from './date-range-picker/calendar-helper';

const PromptsPage = ({
  appName,
  workflowName,
}: {
  appName?: string;
  workflowName?: string;
}) => {
  const searchParams = useSearchParams();
  const [appliedFilters, setAppliedFilters] = useState<FilterCriteriaGroup[]>(
    []
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);
  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [activeTab, setActiveTab] = useState<'time' | 'search'>('time');
  const [selectedPrompt, setSelectedPrompt] = useState<IPrompt[]>([]);
  const [selectedGrouping, setSelectedGrouping] = useState('');

  const [evalConfig, setEvalConfig] = useState<TIdSpecificEvalConfig | null>(
    null
  );
  const [traceId, setTraceId] = useState(searchParams.get('trace_id'));
  const [criteriaPayload, setCriteriaPayload] = useState<CriteriaPayloadType>({
    previousState: [],
    currentState: [],
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [containerVisible, setContainerVisible] = useState(false);

  const [splitPanelOpen, setSplitPanelOpen] = useState(false);
  const [splitActive, setSplitActive] = useState(false);

  const {
    state: { traceIdSearchData },
    clearTraceIDResults,
  } = useTraceId();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const findApp = useContext(FindAppContext);
  const [isCustomEvalFinished, setIsCustomEvalFinished] = useState(false);
  //represents the filter div.
  const leftRef = useRef<HTMLDivElement>(null);
  //represents the data area.
  const rightRef = useRef<HTMLDivElement>(null);
  const [leftHeight, setLeftHeight] = useState(0);

  const [errorResetKey, setErrorResetKey] = useState(0); // Key to trigger reset
  const [isRefreshing, setIsRefreshing] = useState(false); // Track refresh state

  const {
    state,
    clearPrompts,
    handleFormSubmit,
    appDataLoading,
    transformedWorkflows,
    transformedModels,
    transformedServices,
    transformedProviders,
    handleFactNameChange,
    fetchSankeyMetaData,
    fetchFactsData,
    breakdownFilter,
    setBreakdownFilter,
  } = usePrompts();

  const fact_name = state.factName;
  const prevTabRef = useRef(activeTab);

  // Evals response
  const {
    evaluateActions,
    state: evalState,
    dispatch,
  } = useEvals() as EvaluateContextValue;

  // Extract different level evals.
  const { spanEvals, conversationEvals, traceEvals } = evalState;

  const [sankeyMetaDataTreeView, setSankeyMetaDataTreeView] = useState<
    TreeDataItem[]
  >([]);
  const [selectedBreakdownFilter, setSelectedBreakdownFilter] =
    useState<TreeDataItem | null>(null);

  const isSearching =
    activeTab === 'time' ? state.promptLoading : state.promptByTraceIdLoading;
  const sankeyMetadata = state.sankeyMetadata;

  const placeholder = getPlaceholderText(
    selectedBreakdownFilter?.id || null,
    sankeyMetaDataTreeView
  );

  const [multiSelect, setMultiselect] = useState({
    workflow_names: state.queryParams?.workflow_names || '',
    model_names: '',
    services: '',
    providers: '',
  });

  const [dateRange, setDateRange] = useState<{
    start_time: null | string;
    end_time: null | string;
  }>({
    start_time: state.queryParams.start_time,
    end_time: state.queryParams.end_time,
  });

  const { messages } = useLanguage();
  const pathname = usePathname();
  const isComponentPath = pathname.includes('/components/');

  // Error state callback from ErrorBoudary component.
  const handleErrorStateChange = (errorOccurred: boolean) => {
    if (!errorOccurred) {
      setIsRefreshing(false);
    }
  };

  const handleFilterChange = (newFilters: FilterCriteriaGroup[]) =>
    setAppliedFilters(newFilters);

  const handleBreakdownChange = (item: TreeDataItem) => {
    if (isSearching) return;
    setSelectedBreakdownFilter(() => item);
  };

  const handleSearch = (comingFromDatePicker: boolean) => {
    if (comingFromDatePicker) {
      setIsRefreshing(true); // Show error boundary loader when date range changes.
      handleFormSubmit({
        ...dateRange,
      });
      return;
    }
    setBreakdownFilter(selectedBreakdownFilter?.id || null);
    const filterString = constructPromptCriteriaFilterString(appliedFilters);
    handleFormSubmit({
      filter: filterString,
      ...multiSelect,
      ...dateRange,
      breakdown_filter: selectedBreakdownFilter?.id,
      evaluation: criteriaPayload.currentState?.join(';'),
    });
    setCriteriaPayload((prev) => ({
      ...prev,
      previousState: criteriaPayload.currentState,
    }));
  };

  const handleMultiSelect = (key: string, valueSelected: string[]) => {
    setMultiselect((prev) => ({ ...prev, [key]: valueSelected.join(',') }));
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange((prev) => {
      const newRange = {
        ...prev,
        start_time: convertToUTCDate(range.from ? new Date(range.from) : null),
        end_time: convertToUTCDate(range.to ? new Date(range.to) : null),
      };
      return newRange;
    });
  };

  // return true if any of the filters have changed.
  const areFiltersModified = useMemo(() => {
    const filterString = constructPromptCriteriaFilterString(appliedFilters);

    // convert all multiselect strings to arrays for comparison.
    const queryParams = {
      workflow_names: strToArray(state.queryParams?.workflow_names),
      model_names: strToArray(state.queryParams?.model_names),
      services: strToArray(state.queryParams?.services),
      providers: strToArray(state.queryParams?.providers),
    };
    const multiSelectArray = {
      workflow_names: strToArray(multiSelect.workflow_names),
      model_names: strToArray(multiSelect.model_names),
      services: strToArray(multiSelect.services),
      providers: strToArray(multiSelect.providers),
    };

    // Add criteriaPayload check
    const criteriaChanged = shallowComparator(
      criteriaPayload.currentState,
      criteriaPayload.previousState
    );

    return (
      criteriaChanged ||
      shallowComparator(
        queryParams.workflow_names,
        multiSelectArray.workflow_names
      ) ||
      shallowComparator(
        queryParams.model_names,
        multiSelectArray.model_names
      ) ||
      shallowComparator(queryParams.services, multiSelectArray.services) ||
      shallowComparator(queryParams.providers, multiSelectArray.providers) ||
      shallowComparator(
        state.queryParams?.breakdown_filter,
        selectedBreakdownFilter?.id
      ) ||
      shallowComparator(state.queryParams?.filter, filterString)
    );
  }, [
    state.queryParams,
    multiSelect,
    selectedBreakdownFilter,
    appliedFilters,
    criteriaPayload,
  ]);

  // The toolbar button:
  const togglePanel = () => {
    if (splitActive) {
      setSplitPanelOpen((open) => !open); // “in split” mode
    } else {
      setFilterPanelOpen((open) => !open); // “normal” mode
    }
  };

  // Enter split mode:
  const handleSplit = () => {
    setSplitActive(true);
    setSplitPanelOpen(false); // start collapsed
  };

  // Exit split mode:
  const handleCloseSplit = () => {
    setSplitActive(false); // leave filterPanelOpen untouched so we restore the user’s last normal state
  };

  // Decide which one to show:
  const panelVisible = splitActive ? splitPanelOpen : filterPanelOpen;

  useEffect(() => {
    if (!leftRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        // update only if height > 0 (to avoid collapsing when hidden)
        if (newHeight > 0) setLeftHeight(newHeight);
      }
    });

    observer.observe(leftRef.current);
    return () => observer.disconnect();
  }, [panelVisible]);

  useEffect(() => {
    if (rightRef.current && leftHeight) {
      rightRef.current.style.height = `${leftHeight}px`;
    }
  }, [leftHeight]);

  useEffect(() => {
    let breakdownItem: TreeDataItem | null = null;
    if (sankeyMetadata) {
      if (breakdownFilter) {
        const item = findNodeByKey(sankeyMetadata, breakdownFilter);
        if (item !== null) {
          setSankeyMetaDataTreeView(sankeyMetadata);
          breakdownItem = item;
        } else {
          //if not found in sankeyMetadata, its an additional node in sankey and now we show only this.
          const newTreeItem: TreeDataItem = {
            id: breakdownFilter,
            name: breakdownFilter,
          };
          setSankeyMetaDataTreeView([newTreeItem]);
          breakdownItem = newTreeItem;
        }
      } else {
        setSankeyMetaDataTreeView(sankeyMetadata);
        breakdownItem = sankeyMetadata[0];
      }
      //update the breakdownFilter in state, which represents the value in URL as we're going to set breakdownItem in URL.
      if (breakdownItem?.id != breakdownFilter) {
        setBreakdownFilter(breakdownItem?.id || null);
      }
      setSelectedBreakdownFilter(breakdownItem);
    }
  }, [sankeyMetadata]);

  useEffect(() => {
    if (isComponentPath || !fact_name) {
      return;
    }
    fetchSankeyMetaData();
  }, [fact_name]);

  useEffect(() => {
    if (!isComponentPath) fetchFactsData();
  }, []);

  useEffect(() => {
    const prev = prevTabRef.current;
    if (activeTab === 'time') {
      setIsOverlayActive(false);
      clearPrompts();
      // Clear breakdown filter when switching from search tab
      if (prev === 'search') {
        setSelectedBreakdownFilter(null);
        setBreakdownFilter(null);
      }
      // fetch metadata for current fact name
      if (!isComponentPath && fact_name) {
        fetchSankeyMetaData();
      }
      setCriteriaPayload({
        previousState: [],
        currentState: [],
      });
      setSelectedIds([]);
      setContainerVisible(false);
      setAppliedFilters([]);
      setMultiselect({
        workflow_names: '',
        model_names: '',
        services: '',
        providers: '',
      });
      // Reset filters and trigger search
      handleFormSubmit({
        start_time: dateRange.start_time,
        end_time: dateRange.end_time,
        evaluation: '',
        filter: '',
        workflow_names: '',
        model_names: '',
        services: '',
        providers: '',
        breakdown_filter: null,
      });
    } else if (activeTab === 'search') {
      clearTraceIDResults();
      clearPrompts();
    }
    // store current tab value
    prevTabRef.current = activeTab;
  }, [activeTab]);
  // Whenever your data or view changes, recalc totalPages & clamp currentPage
  useEffect(() => {
    if (fact_name !== messages?.General.inferences?.toLowerCase()) {
      const seqData = state?.promptSequenceData ?? [];
      setTotalPages(seqData.length || 1);
      setCurrentPage((p) => Math.min(Math.max(p, 1), seqData.length || 1));
    } else {
      const listLen = state?.promptData?.length ?? 0;
      const pages = Math.max(1, Math.ceil(listLen / itemsPerPage));
      setTotalPages(pages);
      setCurrentPage((p) => Math.min(Math.max(p, 1), pages));
    }
  }, [fact_name, state?.promptData, state?.promptSequenceData]);

  // Clear between tabs
  useEffect(() => {
    if (fact_name !== messages?.General.inferences?.toLowerCase())
      clearPrompts();
    else clearTraceIDResults();
  }, [fact_name]);

  const start_time = state.queryParams.start_time;
  const end_time = state.queryParams.end_time;

  // Convert object to Array of objects.
  const promptsListData = extractData(state.promptSequenceData);

  // Per-job (not all-or-nothing): a job that's still PENDING/RUNNING must not
  // block badges from refreshing for siblings in the same batch that already
  // finished, so this flips true whenever ANY job transitions to a terminal
  // status since the last poll, rather than requiring every job to finish.
  const prevJobStatusesRef = useRef<Record<string, string>>({});
  const [hasNewlyFinishedJob, setHasNewlyFinishedJob] = useState(false);

  useEffect(() => {
    const prevStatuses = prevJobStatusesRef.current;
    const justFinished = evalState.evalJobs.some(
      (job) =>
        (prevStatuses[job.job_id] === 'RUNNING' ||
          prevStatuses[job.job_id] === 'PENDING') &&
        (job.status === 'SUCCEEDED' || job.status === 'FAILED')
    );
    prevJobStatusesRef.current = Object.fromEntries(
      evalState.evalJobs.map((job) => [job.job_id, job.status])
    );
    if (justFinished) setHasNewlyFinishedJob(true);
  }, [evalState.evalJobs]);

  //id specific jobs running.
  useEffect(() => {
    const handleCustomEvalFinished = () => {
      setIsCustomEvalFinished(true);
    };

    const handleEvaluationUpdated = () => {
      setIsCustomEvalFinished(true);
    };

    window.addEventListener('customEvalFinished', handleCustomEvalFinished);
    window.addEventListener('evaluationUpdated', handleEvaluationUpdated);

    return () => {
      window.removeEventListener(
        'customEvalFinished',
        handleCustomEvalFinished
      );
      window.removeEventListener('evaluationUpdated', handleEvaluationUpdated);
    };
  }, []);

  const fetchEvalBadges = useCallback(() => {
    // Components (workflows) are allowed once a workflowName is threaded in;
    // it routes the eval query to workflows/{workflowName}/evals/query.
    if (isComponentPath && !workflowName) return;
    if (
      fact_name !== 'inferences' &&
      (!state.promptSequenceData ||
        state.promptSequenceData === undefined ||
        Object.keys(state.promptSequenceData).length === 0)
    ) {
      return;
    }

    const fact_ids = Object.keys(state.promptSequenceData);
    const isSearchByIdTab = activeTab === 'search';
    const queryStartTime = isSearchByIdTab
      ? undefined
      : (start_time ?? undefined);
    const queryEndTime = isSearchByIdTab ? undefined : (end_time ?? undefined);
    const baseBody: PostEvalsQueryBodyType = {
      fact_name,
      fact_ids,
      start_time: queryStartTime,
      end_time: queryEndTime,
    };

    // Extract span IDs and trace IDs
    const spanIds: string[] = (state.promptData ?? []).map(
      (item: IPrompt) => `${item.trace_id}.${item.span_id}`
    );

    const traceIdsSet = new Set<string>();

    promptsListData.forEach((prompt) => {
      prompt.sequenceData.forEach((item: any) => {
        if (fact_name !== 'inferences' && item.span_id) {
          spanIds.push(`${item.trace_id}.${item.span_id}`);
        }
        if (item.trace_id) {
          traceIdsSet.add(item.trace_id);
        }
      });
    });

    const traceIds: string[] = Array.from(traceIdsSet);

    let app;
    if (appName) app = appName;
    if (findApp) app = findApp;

    // Handle different fact types
    switch (state.factName) {
      case 'traces':
        // Initialize the state variables to initial value
        dispatch({
          type: actionTypes.FETCH_TRACE_EVALS_RESET,
          payload: null,
        });
        dispatch({ type: actionTypes.FETCH_SPAN_EVALS_RESET, payload: null });
        // Make 2 API calls for traces
        traceIds.length > 0 && (app || workflowName)
          ? Promise.allSettled([
              evaluateActions.fetchEvalsTemplate(
                'traces',
                app,
                queryStartTime,
                queryEndTime,
                workflowName
              ),
              evaluateActions.fetchEvalsReport(
                appName || '',
                baseBody,
                workflowName
              ),
              evaluateActions.fetchSpanEvalsReport(
                app!,
                {
                  fact_name: 'inferences',
                  fact_ids: spanIds,
                  start_time: queryStartTime,
                  end_time: queryEndTime,
                },
                workflowName
              ),
            ])
          : null;
        break;

      case 'inferences':
        // Reset the state variable to initial state
        dispatch({ type: actionTypes.FETCH_SPAN_EVALS_RESET, payload: null });
        // Make 2 API calls for inferences: template + report
        spanIds?.length > 0 &&
          (app || workflowName) &&
          Promise.allSettled([
            evaluateActions.fetchEvalsTemplate(
              'inferences',
              app,
              queryStartTime,
              queryEndTime
            ),
            evaluateActions.fetchEvalsReport(
              app!,
              {
                fact_name: 'inferences',
                fact_ids: spanIds,
                start_time: queryStartTime,
                end_time: queryEndTime,
              },
              workflowName
            ),
          ]);
        break;

      default:
        // Reset the state variable to initial data
        dispatch({
          type: actionTypes.FETCH_CONVERSATION_EVALS_RESET,
          payload: null,
        });
        dispatch({
          type: actionTypes.FETCH_TRACE_EVALS_RESET,
          payload: null,
        });
        dispatch({ type: actionTypes.FETCH_INF_EVALS_RESET, payload: null });
        // Make 3 concurrent API calls for conversations and others
        traceIds.length > 0
          ? Promise.allSettled([
              evaluateActions.fetchConversationEvalsReport(
                app!,
                baseBody,
                workflowName
              ),
              evaluateActions.fetchEvalsTemplate(
                'traces',
                app,
                queryStartTime,
                queryEndTime
              ),
              evaluateActions.fetchEvalsReport(
                app!,
                {
                  fact_name: 'traces',
                  fact_ids: traceIds,
                  start_time: queryStartTime,
                  end_time: queryEndTime,
                },
                workflowName
              ),
              evaluateActions.fetchInferenceEvalsReport(
                app!,
                {
                  fact_name: 'inferences',
                  fact_ids: spanIds,
                  start_time: queryStartTime,
                  end_time: queryEndTime,
                },
                workflowName
              ),
            ])
          : null;
        break;
    }
  }, [
    isComponentPath,
    workflowName,
    fact_name,
    state.promptSequenceData,
    state.promptData,
    state.factName,
    start_time,
    end_time,
    activeTab,
    appName,
    findApp,
    promptsListData,
    dispatch,
    evaluateActions,
  ]);

  // Effect 1: fetch eval badges when data arrives (after fact/time-range changes).
  // Intentionally excludes fact_name: when fact changes, clearPrompts() empties
  // promptSequenceData first, triggering the early-return guard. The effect then
  // re-fires once fresh data loads, ensuring we never call the API with stale data.
  useEffect(() => {
    fetchEvalBadges();
  }, [state.promptSequenceData, state.promptData, start_time, end_time]);

  // Effect 2: fetch eval badges as soon as any eval job submitted from the
  // prompts page finishes, even if other jobs in the same batch are still
  // pending/running.
  useEffect(() => {
    if (!hasNewlyFinishedJob && !isCustomEvalFinished) return;

    fetchEvalBadges();
    setHasNewlyFinishedJob(false);
    setIsCustomEvalFinished(false);
  }, [hasNewlyFinishedJob, isCustomEvalFinished]);

  // Time range of the searched trace (search-by-id tab only).
  const evalTraceTimeRange = useMemo(() => {
    if (activeTab !== 'search') return {};
    // A dropdown pick for a different id (e.g. one trace inside a searched
    // conversation) isn't covered by the searched id's range.
    if (evalConfig && evalConfig.id !== traceId) return {};
    // Prefer the trace's own start/end from the traces-by-id API (as the
    // traces page does) over times derived from individual spans.
    const searched: any[] = Array.isArray(traceIdSearchData)
      ? traceIdSearchData
      : [];
    const matchesSearch =
      fact_name !== 'traces' || searched.some((t) => t.trace_id === traceId);
    const apiStart = searched[searched.length - 1]?.start_time;
    const apiEnd = searched[0]?.end_time;
    if (matchesSearch && apiStart && apiEnd) {
      return { startTime: apiStart, endTime: apiEnd };
    }
    if (fact_name !== 'inferences') {
      // by-trace-id data is raw spans keyed by trace id: { [traceId]: spans[] }
      const byTraceId = state?.promptSequenceDataByTraceId;
      return getTraceTimeRange(
        byTraceId && !Array.isArray(byTraceId)
          ? (Object.values(byTraceId).flat() as any[])
          : []
      );
    }
    const prompts = (state?.promptDataByTraceId ?? []) as IPrompt[];
    const starts = prompts.map((p) => new Date(p.start_time).getTime());
    const ends = prompts.map((p) => new Date(p.end_time).getTime());
    const valid = (n: number) => !Number.isNaN(n);
    const s = starts.filter(valid);
    const e = ends.filter(valid);
    return {
      startTime: s.length ? new Date(Math.min(...s)).toISOString() : undefined,
      endTime: e.length ? new Date(Math.max(...e)).toISOString() : undefined,
    };
  }, [
    evalConfig,
    activeTab,
    fact_name,
    state?.promptSequenceDataByTraceId,
    state?.promptDataByTraceId,
    traceIdSearchData,
    traceId,
  ]);

  const handleIdSpecificEvalChange = (
    id: string,
    source: 'conversation' | 'trace'
  ) => {
    setEvalConfig({
      source,
      id,
    });
  };

  const handleRefreshWithErrorRecovery = (updatedTime: any) => {
    setErrorResetKey((prev) => prev + 1);
    handleFormSubmit?.(updatedTime);
  };

  useEffect(() => {
    // When data loads successfully and we were in a refreshing state, clear the loader
    if (isRefreshing && !isSearching) {
      // Check if we have data loaded
      const hasData =
        fact_name !== 'inferences'
          ? state?.promptSequenceData &&
            Object.keys(state.promptSequenceData).length > 0
          : state?.promptData && state.promptData.length > 0;

      if (hasData || !isSearching) {
        setIsRefreshing(false);
      }
    }
  }, [
    isRefreshing,
    isSearching,
    state?.promptSequenceData,
    state?.promptData,
    fact_name,
  ]);

  // whenever dateRange or other Filter changes, reset selection
  useEffect(() => {
    setSelectedPrompt([]);
  }, [
    dateRange.end_time,
    breakdownFilter,
    fact_name,
    currentPage,
    areFiltersModified,
  ]);

  return (
    <>
      <ToolbarContainer
        onToggle={togglePanel}
        iconSrc={
          filterPanelOpen
            ? '/shared-icons/filter-collapse.svg'
            : '/shared-icons/filter-expand.svg'
        }
        handleDateRangeChange={handleDateRangeChange}
        handleApply={() => {
          handleSearch(true);
        }}
        setIsOverlayActive={setIsOverlayActive}
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        selectedFact={fact_name}
        onSelectFact={(fact) => {
          // Reset all criteria-related state when fact changes
          setCriteriaPayload({
            previousState: [],
            currentState: [],
          });
          setSelectedIds([]);
          setContainerVisible(false);
          setMultiselect({
            workflow_names: '',
            model_names: '',
            services: '',
            providers: '',
          });

          handleFactNameChange(fact);
        }}
        factsOptions={state.factsData}
        isPromptsPage={true}
        hidePagination={selectedGrouping.length > 0}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        promptList={
          fact_name !== messages?.General.inferences?.toLowerCase()
            ? (promptsListData ?? [])
            : (state.promptData ?? [])
        }
        itemsPerPage={
          fact_name !== messages?.General.inferences?.toLowerCase()
            ? 1
            : itemsPerPage
        }
        isLoading={isSearching}
        isFactsLoading={isSearching}
        evaluateOpen={evaluateOpen}
        setEvaluateOpen={setEvaluateOpen}
        traceId={traceId}
        setTraceId={setTraceId}
        setSelectedPrompt={setSelectedPrompt}
        handleFormSubmit={handleFormSubmit}
        onRefreshWithErrorRecovery={handleRefreshWithErrorRecovery}
      />

      {/* left panel filter. */}
      <div className=" mb-5 mt-3 flex rounded-lg border border-line-secondary bg-card-00">
        {panelVisible && activeTab === 'time' && (
          <div
            className={cn(
              'relative w-[280px] overflow-y-auto',
              isOverlayActive && 'pointer-events-none'
            )}
            ref={leftRef}
          >
            <div className="space-y-6 p-5">
              <EvalCriteriaFilter
                inferTitle={fact_name}
                setCriteriaPayload={setCriteriaPayload}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                containerVisible={containerVisible}
                setContainerVisible={setContainerVisible}
                appName={appName}
                workflowName={workflowName}
                startTime={start_time || undefined}
                endTime={end_time || undefined}
              />
              <div>
                <p className="mb-2 flex items-center gap-1">
                  <span className="text-form-input-text text-body-primary">
                    {messages?.General?.prompts}
                  </span>
                  <span>{System.InfoIcon('size-3 text-[#257C9D]')}</span>
                </p>
                {appDataLoading ? (
                  <Skeleton className="animate-pulse-custom h-10 rounded-lg bg-[#8C9EA1]" />
                ) : (
                  <AddPromptCriteriaFilters
                    key={fact_name}
                    onFilterChange={handleFilterChange}
                  />
                )}
              </div>
              {!isComponentPath && sankeyMetaDataTreeView.length > 0 && (
                <div className="space-y-2">
                  <p className="text-form-input-text text-body-primary">
                    {messages?.General?.breakdown}
                  </p>

                  <TreeDropdown
                    data={sankeyMetaDataTreeView}
                    selectedItem={selectedBreakdownFilter?.id || null}
                    onSelect={handleBreakdownChange}
                    className="w-full"
                    disabled={isSearching}
                  />
                </div>
              )}
              {!isComponentPath && (
                <div className="space-y-2">
                  <p className="text-form-input-text text-body-primary">
                    {messages?.PromptFilters.multi_select.workflow}
                  </p>
                  {appDataLoading ? (
                    <Skeleton className="animate-pulse-custom h-10 rounded-lg bg-[#8C9EA1]" />
                  ) : (
                    <MultiSelect
                      key={fact_name}
                      options={transformedWorkflows}
                      onValueChange={(valueSelected) =>
                        handleMultiSelect('workflow_names', valueSelected)
                      }
                      defaultValue={
                        multiSelect.workflow_names
                          ? multiSelect.workflow_names.split(',')
                          : []
                      }
                      placeholder={
                        messages?.PromptFilters.multi_select?.select_workflow
                      }
                      variant="inverted"
                      maxCount={2}
                      loading={appDataLoading}
                      maxOptions={5}
                    />
                  )}
                </div>
              )}
              <div className="space-y-2">
                <p className="text-form-input-text text-body-primary">
                  {messages?.General?.model}
                </p>
                {appDataLoading ? (
                  <Skeleton className="animate-pulse-custom h-10 rounded-lg bg-[#8C9EA1]" />
                ) : (
                  <MultiSelect
                    key={fact_name}
                    options={removeFilterDuplicates(transformedModels)}
                    onValueChange={(valueSelected) =>
                      handleMultiSelect('model_names', valueSelected)
                    }
                    placeholder={
                      messages?.PromptFilters.multi_select.select_model
                    }
                    variant="inverted"
                    maxCount={2}
                    loading={appDataLoading}
                    maxOptions={5}
                  />
                )}
              </div>
              <div className="space-y-2">
                <p className="text-form-input-text text-body-primary">
                  {messages?.General?.service}
                </p>
                {appDataLoading ? (
                  <Skeleton className="animate-pulse-custom h-10 rounded-lg bg-[#8C9EA1]" />
                ) : (
                  <MultiSelect
                    key={fact_name}
                    options={transformedServices}
                    onValueChange={(valueSelected) =>
                      handleMultiSelect('services', valueSelected)
                    }
                    placeholder={
                      messages?.PromptFilters.multi_select.select_service
                    }
                    variant="inverted"
                    maxCount={2}
                    loading={appDataLoading}
                    maxOptions={5}
                  />
                )}
              </div>
              <div className="space-y-2">
                <p className="text-form-input-text text-body-primary">
                  {messages?.General?.provider}
                </p>
                {appDataLoading ? (
                  <Skeleton className="animate-pulse-custom h-10 rounded-lg bg-[#8C9EA1]" />
                ) : (
                  <MultiSelect
                    key={fact_name}
                    options={transformedProviders}
                    onValueChange={(valueSelected) =>
                      handleMultiSelect('providers', valueSelected)
                    }
                    placeholder={
                      messages?.PromptFilters.multi_select.select_provider
                    }
                    variant="inverted"
                    maxCount={2}
                    loading={appDataLoading}
                    maxOptions={5}
                  />
                )}
              </div>
              <CustomButton
                customVariant="primary"
                customSize="large"
                onClick={() => handleSearch(false)}
                className="w-full"
                disabled={appDataLoading || !areFiltersModified}
              >
                {messages?.General?.search}
              </CustomButton>
            </div>
          </div>
        )}

        <div
          className={cn(
            'flex-1 overflow-y-auto',
            filterPanelOpen &&
              activeTab === 'time' &&
              'border-l border-line-secondary'
          )}
          ref={rightRef}
        >
          {fact_name !== 'inferences' ? (
            <SplitPromptSequenceProvider>
              {isRefreshing || isSearching ? (
                <div className="flex h-full items-center justify-center">
                  <Loader
                    title={messages?.PromptsListing.getting_prompts}
                    description={messages?.General.please_wait}
                  />
                </div>
              ) : (
                <ErrorBoundary
                  customErrorMessage={
                    messages?.ErrorBoundary.prompts_error || ''
                  }
                  className="h-full w-full"
                  onError={handleErrorStateChange}
                  resetKey={errorResetKey}
                >
                  <PromptSequenceView
                    data={
                      activeTab === 'time'
                        ? (state?.promptSequenceData ?? [])
                        : (state?.promptSequenceDataByTraceId ?? [])
                    }
                    currentPageIndex={currentPage - 1}
                    loading={isSearching}
                    onSplit={handleSplit}
                    onCloseSplit={handleCloseSplit}
                    factName={fact_name}
                    spanEvals={spanEvals}
                    traceEvals={traceEvals}
                    conversationEvals={conversationEvals}
                    setEvaluateOpen={setEvaluateOpen}
                    handleIdSpecificEvalChange={handleIdSpecificEvalChange}
                    appName={appName}
                    workflowName={workflowName}
                    filterStartTime={dateRange?.start_time}
                    filterEndTime={dateRange?.end_time}
                  />
                </ErrorBoundary>
              )}
            </SplitPromptSequenceProvider>
          ) : fact_name === 'inferences' ||
            activeTab === 'time' ||
            activeTab === 'search' ? (
            isRefreshing || isSearching ? (
              <div className="flex h-full items-center justify-center">
                <Loader
                  title={messages?.PromptsListing.getting_prompts}
                  description={messages?.General.please_wait}
                />
              </div>
            ) : (
              <ErrorBoundary
                customErrorMessage={messages?.ErrorBoundary.prompts_error || ''}
                className="h-full w-full"
                onError={handleErrorStateChange}
                resetKey={errorResetKey}
              >
                <PromptsListing
                  data={
                    activeTab === 'time'
                      ? state?.promptData
                      : state?.promptDataByTraceId
                  }
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  selectedPrompt={selectedPrompt}
                  setSelectedPrompt={setSelectedPrompt}
                  appName={appName ?? ''}
                  workflowName={workflowName}
                  activeTab={activeTab}
                  selectedGrouping={selectedGrouping}
                  setSelectedGrouping={setSelectedGrouping}
                />
              </ErrorBoundary>
            )
          ) : (
            <PromptsTraceIdSearchTab
              data={state?.promptData}
              loading={state?.promptLoading}
            />
          )}
        </div>
      </div>

      {evaluateOpen && (
        <EvalModal
          inferTitle={fact_name}
          open={evaluateOpen}
          setOpen={setEvaluateOpen}
          breakdownFilter={selectedBreakdownFilter?.id || null}
          selectedPrompt={selectedPrompt}
          placeholder={placeholder}
          traceId={
            evalConfig ? evalConfig.id : activeTab === 'search' ? traceId : null
          }
          source={evalConfig ? evalConfig.source : 'trace'}
          appName={appName}
          workflowName={workflowName}
          setEvalConfig={setEvalConfig}
          evalFilters={criteriaPayload.currentState?.join(';') || null}
          isSearchByIdTab={activeTab === 'search'}
          traceStartTime={evalTraceTimeRange.startTime}
          traceEndTime={evalTraceTimeRange.endTime}
        />
      )}
    </>
  );
};

export { PromptsPage };
